import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Flashbar,
  FormField,
  Header,
  RadioGroup,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import {
  deleteAiArchetypeFeedback,
  getAiArchetypeFeedback,
  putAiArchetypeFeedback,
} from '../services/s3Service';
import {
  ARCHETYPE_NAMES,
  D_SUBTYPE_LABELS,
  confidenceLabel,
  fundingRatioLabel,
  readAiArchetypeBody,
} from '../utils/aiArchetypeDisplay';

/**
 * AI archetype recommendation (P547836103), at the top of the Move to AI tab.
 *
 * Presentation only. The FR page renders this panel only for a record that
 * passes isAiArchetypeDisplayEligible; every value below is one the MAP agent
 * persisted, formatted and labelled here. There are no scores, no adjusted
 * funding, no override and no routing controls: reviewers may only record
 * latest-only agree / disagree feedback on the current evaluation.
 */

export const ADVISORY_STATEMENT =
  'This recommendation does not change the current Chronos MAP decision or funding calculation.';
export const UNAVAILABLE_TEXT = 'AI archetype recommendation unavailable';
export const EARLIER_EVALUATION_TEXT = 'Feedback on an earlier evaluation';
export const LATEST_ONLY_HELP =
  'Archetype feedback is latest-only. Saving replaces your previous archetype feedback for this fund '
  + 'request, and removing it clears it from the current feedback record; earlier values can remain in '
  + 'audit records. Feedback never changes the recommendation, the MAP decision or funding.';
export const RESULT_CHANGED_TEXT =
  'This recommendation changed after the page loaded, so your feedback was not saved. Reload to '
  + 'review the current evaluation.';
export const ANNOTATION_CHANGED_TEXT =
  'Your archetype feedback changed in another session, so this change was not saved. The latest saved '
  + 'feedback is shown below.';
export const NOT_AVAILABLE_TEXT = 'Feedback is not available for this recommendation.';

const FEEDBACK_LABELS = { AGREE: 'Agree', DISAGREE: 'Disagree' };
const ARCHETYPE_OPTIONS = Object.entries(ARCHETYPE_NAMES).map(([code, name]) => ({
  value: code,
  label: code === 'NON_AI' ? name : `${code}: ${name}`,
}));
const D_SUBTYPE_OPTIONS = Object.entries(D_SUBTYPE_LABELS).map(([value, label]) => ({ value, label }));

const humanize = (code) => {
  const text = String(code).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const percentFromBasisPoints = (value) => (Number.isInteger(value) ? `${(value / 100).toFixed(2)}%` : '—');
const moneyFromCents = (value, currency) => (
  Number.isInteger(value) && currency
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value / 100)
    : '—'
);
const recommendedText = (archetype) => {
  const label = archetype.code === 'NON_AI' ? archetype.name : `${archetype.code}: ${archetype.name}`;
  return archetype.d_subtype ? `${label} (${D_SUBTYPE_LABELS[archetype.d_subtype]})` : label;
};
const archetypeText = (code, dSubtype) => {
  const label = ARCHETYPE_OPTIONS.find((option) => option.value === code)?.label || code;
  return dSubtype ? `${label} (${D_SUBTYPE_LABELS[dSubtype] || dSubtype})` : label;
};

const KeyValue = ({ label, children }) => (
  <div>
    <Box variant="awsui-key-label">{label}</Box>
    <div>{children}</div>
  </div>
);

const NOT_AVAILABLE = 'Not available';

const SignalList = ({ signals }) => {
  if (!signals) return NOT_AVAILABLE;
  if (signals.length === 0) return 'None';
  return (
    <ul style={{ marginTop: 0, paddingLeft: 20 }}>
      {signals.map((signal, index) => (
        <li key={`${index}|${signal.rule_id}`}>
          {signal.label}
          {signal.fields.length > 0 && <Box variant="small" display="inline">{` (${signal.fields.join(', ')})`}</Box>}
        </li>
      ))}
    </ul>
  );
};

const axisText = (axis) => {
  if (!axis) return '—';
  const reasons = [
    ...(Array.isArray(axis.reason_codes) ? axis.reason_codes : []),
    ...(typeof axis.reason_code === 'string' && axis.reason_code ? [axis.reason_code] : []),
  ];
  const status = axis.status === null ? 'Unknown' : humanize(axis.status);
  return reasons.length > 0 ? `${status} (${reasons.join(', ')})` : status;
};

const FundingRatio = ({ ratio }) => {
  if (!ratio) return NOT_AVAILABLE;
  const type = ratio.status === 'pass' ? 'success' : ratio.status === 'fail' ? 'warning' : 'info';
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator type={type}>{fundingRatioLabel(ratio.status)}</StatusIndicator>
      <Box variant="small">
        {`Bedrock and AgentCore share ${percentFromBasisPoints(ratio.share_basis_points)}, threshold `
          + `${percentFromBasisPoints(ratio.threshold_basis_points)}`
          + `${ratio.effective_date ? `, effective ${ratio.effective_date}` : ''}. Display only; it never changes funding.`}
      </Box>
    </SpaceBetween>
  );
};

const LINE_TREATMENT_COLUMNS = (currency) => [
  { id: 'label', header: 'Line', cell: (item) => item.label || item.service_code || '—' },
  { id: 'arr', header: 'Qualification ARR', cell: (item) => moneyFromCents(item.qualification_arr_cents, currency) },
  { id: 'qualification', header: 'Qualification', cell: (item) => axisText(item.qualification) },
  { id: 'credit', header: 'Credit', cell: (item) => axisText(item.credit) },
  { id: 'ai', header: 'AI Included Services', cell: (item) => axisText(item.ai_included_services) },
];

const VersionDetails = ({ record }) => (
  <ExpandableSection headerText="Evaluation and version details">
    <ColumnLayout columns={2} variant="text-grid">
      <KeyValue label="Evaluation ID">{record.evaluation_id}</KeyValue>
      <KeyValue label="Evaluated at">{record.evaluated_at}</KeyValue>
      <KeyValue label="Calculator">
        {record.calculator.effective_calculator_id || '—'}
        {record.calculator.repriced_calculator_used
          ? ` (repriced from ${record.calculator.source_calculator_id || '—'})`
          : ''}
      </KeyValue>
      <KeyValue label="Producer">
        {`Schema ${record.producer.schema_version}, config ${record.producer.config_digest}`}
      </KeyValue>
      <KeyValue label="Ruleset">
        {`${record.classifier.ruleset_version}, config ${record.classifier.config_digest}`}
      </KeyValue>
      <KeyValue label="Included Services List">
        {`Policy ${record.services_list.policy_version}, source version ${record.services_list.source_version_id}, config ${record.services_list.config_digest}`}
      </KeyValue>
      <KeyValue label="Input digest">{record.input_digest}</KeyValue>
    </ColumnLayout>
  </ExpandableSection>
);

const isAnnotation = (value) => value !== null
  && typeof value === 'object'
  && Object.prototype.hasOwnProperty.call(FEEDBACK_LABELS, value.feedback)
  && typeof value.evaluation_id === 'string'
  && Number.isInteger(value.annotation_version);

const annotationText = (annotation) => {
  const base = FEEDBACK_LABELS[annotation.feedback];
  if (annotation.feedback !== 'DISAGREE' || !annotation.corrected_archetype) return base;
  return `${base}, corrected to ${archetypeText(annotation.corrected_archetype, annotation.corrected_d_subtype)}`;
};

const SavedAnnotation = ({ annotation }) => (
  <SpaceBetween size="xxs">
    <div>{annotationText(annotation)}</div>
    {(annotation.annotated_at || annotation.annotated_by) && (
      <Box variant="small">
        {[annotation.annotated_by, annotation.annotated_at].filter(Boolean).join(', ')}
      </Box>
    )}
  </SpaceBetween>
);

const AIArchetypeFeedback = ({ fundRequestId, record, onReloadPage }) => {
  // With no recommended archetype there is nothing to agree with; a reviewer
  // can still record the archetype they would assign.
  const canAgree = record.archetype !== null && record.archetype !== undefined;
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);
  const [annotation, setAnnotation] = useState(null);
  const [serverStale, setServerStale] = useState(false);
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [corrected, setCorrected] = useState(null);
  const [dSubtype, setDSubtype] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const resetControls = () => {
    setEditing(false);
    setFeedback(null);
    setCorrected(null);
    setDSubtype(null);
  };

  const handleError = (err, action) => {
    if (err.status === 409 && err.code === 'AI_ARCHETYPE_RESULT_CHANGED') {
      setFlash({ type: 'warning', content: RESULT_CHANGED_TEXT, reload: true });
      return false;
    }
    if (err.status === 409 && err.code === 'AI_ARCHETYPE_ANNOTATION_CHANGED') {
      setFlash({ type: 'info', content: ANNOTATION_CHANGED_TEXT });
      return true;
    }
    if (err.status === 409 && /^Blind review pending/.test(err.message || '')) {
      setFlash({ type: 'info', content: err.message, reload: true });
      return false;
    }
    if (err.status === 404 && err.code === 'AI_ARCHETYPE_NOT_AVAILABLE') {
      setNotAvailable(true);
      return false;
    }
    setFlash({ type: 'error', content: `Failed to ${action} archetype feedback: ${err.message}` });
    return false;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await getAiArchetypeFeedback(fundRequestId);
      const saved = data?.annotation ?? null;
      if (saved !== null && !isAnnotation(saved)) throw new Error('Saved archetype feedback could not be read.');
      setAnnotation(saved);
      setServerStale(data?.stale === true);
    } catch (err) {
      if (err.status === 404 && err.code === 'AI_ARCHETYPE_NOT_AVAILABLE') {
        setNotAvailable(true);
      } else if (err.status === 409 && /^Blind review pending/.test(err.message || '')) {
        setFlash({ type: 'info', content: err.message, reload: true });
        setLoadFailed(true);
      } else {
        setLoadFailed(true);
      }
    } finally {
      setLoading(false);
    }
  }, [fundRequestId]);

  useEffect(() => {
    load();
  }, [load]);

  // A saved annotation on another evaluation is shown read-only and never
  // prefills the controls for this one; new feedback creates a fresh pointer.
  const stale = annotation !== null && (serverStale || annotation.evaluation_id !== record.evaluation_id);
  const current = annotation !== null && !stale ? annotation : null;

  const startEditing = () => {
    setFeedback(current.feedback);
    setCorrected(current.corrected_archetype || null);
    setDSubtype(current.corrected_d_subtype || null);
    setEditing(true);
    setFlash(null);
  };

  const canSubmit = (feedback === 'AGREE' && canAgree)
    || (feedback === 'DISAGREE' && corrected !== null && (corrected !== 'D' || dSubtype !== null));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setFlash(null);
    try {
      await putAiArchetypeFeedback(fundRequestId, {
        expected_evaluation_id: record.evaluation_id,
        expected_input_digest: record.input_digest,
        expected_annotation_version: current ? current.annotation_version : null,
        feedback,
        corrected_archetype: feedback === 'DISAGREE' ? corrected : null,
        corrected_d_subtype: feedback === 'DISAGREE' && corrected === 'D' ? dSubtype : null,
      });
      resetControls();
      setFlash({ type: 'success', content: 'Archetype feedback saved.' });
      await load();
    } catch (err) {
      if (handleError(err, 'save')) {
        resetControls();
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setFlash(null);
    try {
      await deleteAiArchetypeFeedback(fundRequestId, current.evaluation_id, current.annotation_version);
      resetControls();
      setFlash({ type: 'success', content: 'Archetype feedback removed.' });
      await load();
    } catch (err) {
      if (handleError(err, 'remove')) {
        resetControls();
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const flashItems = flash ? [{
    id: 'ai-archetype-feedback',
    type: flash.type,
    content: flash.content,
    dismissible: true,
    onDismiss: () => setFlash(null),
    action: flash.reload && onReloadPage ? <Button onClick={onReloadPage}>Reload</Button> : undefined,
  }] : [];

  let body;
  if (loading) {
    body = <StatusIndicator type="loading">Loading archetype feedback</StatusIndicator>;
  } else if (notAvailable) {
    body = <Box>{NOT_AVAILABLE_TEXT}</Box>;
  } else if (loadFailed) {
    body = (
      <SpaceBetween size="xs" direction="horizontal">
        <StatusIndicator type="error">Archetype feedback could not be loaded.</StatusIndicator>
        <Button onClick={load}>Retry</Button>
      </SpaceBetween>
    );
  } else {
    const showControls = editing || current === null;
    body = (
      <SpaceBetween size="s">
        {stale && (
          <KeyValue label={EARLIER_EVALUATION_TEXT}>
            <SavedAnnotation annotation={annotation} />
          </KeyValue>
        )}
        {current && !editing && (
          <KeyValue label="Your feedback on this evaluation">
            <SpaceBetween size="xs">
              <SavedAnnotation annotation={current} />
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={startEditing} disabled={busy}>Edit</Button>
                <Button onClick={handleRemove} disabled={busy}>Remove</Button>
              </SpaceBetween>
            </SpaceBetween>
          </KeyValue>
        )}
        {showControls && (
          <SpaceBetween size="s">
            <FormField
              label={canAgree
                ? 'Do you agree with the recommended archetype?'
                : 'No archetype was recommended. Disagree to record the archetype you would assign.'}
            >
              <RadioGroup
                ariaLabel="Archetype feedback"
                value={feedback}
                onChange={({ detail }) => setFeedback(detail.value)}
                items={[
                  ...(canAgree ? [{ value: 'AGREE', label: FEEDBACK_LABELS.AGREE }] : []),
                  { value: 'DISAGREE', label: FEEDBACK_LABELS.DISAGREE },
                ]}
              />
            </FormField>
            {feedback === 'DISAGREE' && (
              <FormField label="Corrected archetype">
                <Select
                  ariaLabel="Corrected archetype"
                  selectedOption={ARCHETYPE_OPTIONS.find((option) => option.value === corrected) || null}
                  onChange={({ detail }) => setCorrected(detail.selectedOption.value)}
                  options={ARCHETYPE_OPTIONS}
                  placeholder="Choose an archetype"
                />
              </FormField>
            )}
            {feedback === 'DISAGREE' && corrected === 'D' && (
              <FormField label="D subtype">
                <Select
                  ariaLabel="D subtype"
                  selectedOption={D_SUBTYPE_OPTIONS.find((option) => option.value === dSubtype) || null}
                  onChange={({ detail }) => setDSubtype(detail.selectedOption.value)}
                  options={D_SUBTYPE_OPTIONS}
                  placeholder="Choose a D subtype"
                />
              </FormField>
            )}
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit || busy} loading={busy}>
                Save archetype feedback
              </Button>
              {editing && <Button onClick={resetControls} disabled={busy}>Cancel</Button>}
            </SpaceBetween>
          </SpaceBetween>
        )}
      </SpaceBetween>
    );
  }

  return (
    <ExpandableSection headerText="Your archetype feedback" defaultExpanded>
      <SpaceBetween size="s">
        <Box variant="small">{LATEST_ONLY_HELP}</Box>
        <Flashbar items={flashItems} />
        {body}
      </SpaceBetween>
    </ExpandableSection>
  );
};

const AIArchetypePanel = ({ record, fundRequestId, onReloadPage }) => {
  const header = <Header variant="h2" description="Advisory recommendation">AI Archetype Recommendation</Header>;
  const view = readAiArchetypeBody(record);

  if (!view) {
    return (
      <Container header={header}>
        <SpaceBetween size="s">
          <StatusIndicator type="info">{UNAVAILABLE_TEXT}</StatusIndicator>
          <Box>{ADVISORY_STATEMENT}</Box>
        </SpaceBetween>
      </Container>
    );
  }

  const metrics = view.metrics;
  const currency = metrics?.currency;
  return (
    <Container header={header}>
      <SpaceBetween size="m">
        <Alert type="info">{ADVISORY_STATEMENT}</Alert>
        {view.evaluation_status === 'needs_review' && (
          <StatusIndicator type="warning">Needs human review: the classifier could not complete this evaluation.</StatusIndicator>
        )}
        {view.evaluation_status === 'not_applicable' && (
          <StatusIndicator type="info">Not applicable: the calculator has no included Bedrock or AgentCore ARR.</StatusIndicator>
        )}
        <ColumnLayout columns={3} variant="text-grid">
          <KeyValue label="Recommended archetype">
            {view.archetype
              ? recommendedText(view.archetype)
              : view.evaluation_status === 'not_applicable' ? 'Not applicable' : NOT_AVAILABLE}
          </KeyValue>
          <KeyValue label="Confidence">{view.confidence ? confidenceLabel(view.confidence) : NOT_AVAILABLE}</KeyValue>
          <KeyValue label="Advisory MAP treatment">
            {view.map_treatment_recommendation ? view.map_treatment_recommendation.label : NOT_AVAILABLE}
          </KeyValue>
          <KeyValue label="Source type">
            {view.source ? view.source.source_type || 'None identified' : NOT_AVAILABLE}
          </KeyValue>
          <KeyValue label="Suggested reviewer action">
            {view.suggested_reviewer_action ? humanize(view.suggested_reviewer_action) : NOT_AVAILABLE}
          </KeyValue>
          <KeyValue label="41% Bedrock and AgentCore funding-ratio check (advisory)">
            <FundingRatio ratio={view.funding_ratio} />
          </KeyValue>
        </ColumnLayout>
        {view.source?.source_data_gap && (
          <StatusIndicator type="warning">
            Source-data gap: no structured source value was available, so the source comes from text evidence.
          </StatusIndicator>
        )}
        {view.source && (
          <KeyValue label="Source evidence"><SignalList signals={view.source.evidence} /></KeyValue>
        )}
        {metrics ? (
          <SpaceBetween size="xs">
            <Box variant="small">{`Shares of ${humanize(metrics.basis)}, in ${currency || 'an unknown currency'}`}</Box>
            <ColumnLayout columns={3} variant="text-grid">
              <KeyValue label="Bedrock and AgentCore share">{percentFromBasisPoints(metrics.bedrock_share_basis_points)}</KeyValue>
              <KeyValue label="ML-infrastructure share">{percentFromBasisPoints(metrics.ml_infra_share_basis_points)}</KeyValue>
              <KeyValue label="GPU share">{percentFromBasisPoints(metrics.gpu_share_basis_points)}</KeyValue>
              <KeyValue label="DB&A share">{percentFromBasisPoints(metrics.dba_share_basis_points)}</KeyValue>
              <KeyValue label="Recorded Qualification ARR">{moneyFromCents(metrics.qualification_arr_cents, currency)}</KeyValue>
              <KeyValue label="Reconciliation delta">{moneyFromCents(metrics.legacy_reconciliation_delta_cents, currency)}</KeyValue>
            </ColumnLayout>
          </SpaceBetween>
        ) : (
          <KeyValue label="Shares">{NOT_AVAILABLE}</KeyValue>
        )}
        <ColumnLayout columns={2} variant="text-grid">
          <KeyValue label="Deciding signals"><SignalList signals={view.deciding_signals} /></KeyValue>
          <KeyValue label="Contradictions"><SignalList signals={view.contradictions} /></KeyValue>
        </ColumnLayout>
        <KeyValue label="Line treatments">
          {!view.line_treatments && NOT_AVAILABLE}
          {view.line_treatments && view.line_treatments.length === 0 && 'None'}
          {view.line_treatments && view.line_treatments.length > 0 && (
            <Table
              variant="embedded"
              columnDefinitions={LINE_TREATMENT_COLUMNS(currency)}
              items={view.line_treatments}
            />
          )}
        </KeyValue>
        <VersionDetails record={view} />
        <AIArchetypeFeedback
          key={view.evaluation_id}
          fundRequestId={fundRequestId}
          record={view}
          onReloadPage={onReloadPage}
        />
      </SpaceBetween>
    </Container>
  );
};

export default AIArchetypePanel;
